import { byIdsResultJSONZ, type ByIdsResultJSON } from "_shared/_api/by-ids/by-ids-result";
import PaginatedResponse, { type Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import Guid from "_shared/guids/guid";
import type CompanyBookmarkQuery from "marketplace/bookmarks/company-bookmarks/_api/queries/company-bookmark-query";
import BookmarkedCompanyListItemSchema, { BookmarkedCompanyListItemJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-list-item-schema";
import BookmarkedCompanySchema, { BookmarkedCompanyJSON } from "marketplace/bookmarks/company-bookmarks/_schema/bookmarked-company-schema";
import type IndividualBookmarkQuery from "marketplace/bookmarks/individual-bookmarks/_api/queries/individual-bookmark-query";
import BookmarkedIndividualListItemSchema, { BookmarkedIndividualListItemJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-list-item-schema";
import BookmarkedIndividualSchema, { BookmarkedIndividualJSON } from "marketplace/bookmarks/individual-bookmarks/_schema/bookmarked-individual-schema";
import type TeamBookmarkQuery from "marketplace/bookmarks/team-bookmarks/_api/queries/team-bookmark-query";
import BookmarkedTeamListItemSchema, { BookmarkedTeamListItemJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-list-item-schema";
import BookmarkedTeamSchema, { BookmarkedTeamJSON } from "marketplace/bookmarks/team-bookmarks/_schema/bookmarked-team-schema";
import { z } from "zod";

/**
 * `append` tells the bookmarks slice to add the page to the pages already loaded for the same
 * search (infinite scroll), not to replace them.
 */
export type IndividualBookmarkListArgs = {
  query: IndividualBookmarkQuery;
  append?: boolean;
};

export type CompanyBookmarkListArgs = {
  query: CompanyBookmarkQuery;
};

export type TeamBookmarkListArgs = {
  query: TeamBookmarkQuery;
};

type BookmarkTag = { type: "Bookmark"; id: string };

type CompanyTag = { type: "Company"; id: string };

type TeamTag = { type: "Team"; id: string };

/**
 * The Bookmark LIST tag is the one that the create and delete mutations invalidate. A page also
 * carries each row's company, and its search, filters and order read the company's name, so it
 * provides the Company tags too: an edit to any company may change what the page shows, matches or
 * how it orders, and the company mutations invalidate Company LIST or the company's own tag.
 */
export function getCompanyBookmarkListTags(result?: Paginated<BookmarkedCompanyListItemJSON>): (BookmarkTag | CompanyTag)[] {
  return [
    ...(result?.data.map(({ id }) => ({ type: "Bookmark" as const, id })) ?? []),
    ...(result?.data.map(({ companyId }) => ({ type: "Company" as const, id: companyId })) ?? []),
    { type: "Bookmark", id: "LIST" },
    { type: "Company", id: "LIST" },
  ];
}

/** The LIST tag refreshes the answer when a bookmark is created or deleted, because it may be one of these companies. */
export function getCompanyBookmarksByCompanyIdsTags(companyIds: Guid[]): BookmarkTag[] {
  return [
    ...companyIds.map((companyId) => ({ type: "Bookmark" as const, id: companyId.value })),
    { type: "Bookmark", id: "LIST" },
  ];
}

/**
 * The Bookmark LIST tag is the one that the create and delete mutations invalidate. A page also
 * carries each row's team, and its search, filters and order read the team's name and leader, so it
 * provides the Team tags too. A team's own tag refreshes a page that shows it. Team LIST refreshes
 * every page, because an edit to a team the page left out can bring it in or move other rows: every
 * team mutation that can change a name, a leader or who may read the team invalidates it.
 */
export function getTeamBookmarkListTags(result?: Paginated<BookmarkedTeamListItemJSON>): (BookmarkTag | TeamTag)[] {
  return [
    ...(result?.data.map(({ id }) => ({ type: "Bookmark" as const, id })) ?? []),
    ...(result?.data.map(({ teamId }) => ({ type: "Team" as const, id: teamId })) ?? []),
    { type: "Bookmark", id: "LIST" },
    { type: "Team", id: "LIST" },
  ];
}

/** The LIST tag refreshes the answer when a bookmark is created or deleted, because it may be one of these teams. */
export function getTeamBookmarksByTeamIdsTags(teamIds: Guid[]): BookmarkTag[] {
  return [
    ...teamIds.map((teamId) => ({ type: "Bookmark" as const, id: teamId.value })),
    { type: "Bookmark", id: "LIST" },
  ];
}

/** The LIST tag is the one that the create and delete mutations invalidate. */
export function getIndividualBookmarkListTags(result?: Paginated<BookmarkedIndividualListItemJSON>): BookmarkTag[] {
  return [
    ...(result?.data.map(({ id }) => ({ type: "Bookmark" as const, id })) ?? []),
    { type: "Bookmark", id: "LIST" },
  ];
}

/** The LIST tag refreshes the answer when a bookmark is created or deleted, because it may be one of these people. */
export function getIndividualBookmarksByIndividualIdsTags(individualIds: Guid[]): BookmarkTag[] {
  return [
    ...individualIds.map((individualId) => ({ type: "Bookmark" as const, id: individualId.value })),
    { type: "Bookmark", id: "LIST" },
  ];
}

export const bookmarksApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Queries
    getCompanyBookmarks: build.query<Paginated<BookmarkedCompanyListItemJSON>, CompanyBookmarkListArgs>({
      query: ({ query }) => ({
        url: "marketplace/company-bookmarks",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(BookmarkedCompanyListItemSchema.BookmarkedCompanyListItemJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (
        response: BookmarkedCompanyListItemJSON[],
        meta,
      ): Paginated<BookmarkedCompanyListItemJSON> => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
      providesTags: getCompanyBookmarkListTags,
    }),
    getCompanyBookmarksByCompanyIds: build.query<ByIdsResultJSON<BookmarkedCompanyJSON>, { companyIds: Guid[] }>({
      query: ({ companyIds }) => ({
        url: "marketplace/company-bookmarks/by-company-ids",
        method: "POST",
        body: { companyIds: companyIds.map((companyId) => companyId.value) },
      }),
      rawResponseSchema: byIdsResultJSONZ(BookmarkedCompanySchema.BookmarkedCompanyJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { companyIds }) => getCompanyBookmarksByCompanyIdsTags(companyIds),
    }),
    getIndividualBookmarks: build.query<Paginated<BookmarkedIndividualListItemJSON>, IndividualBookmarkListArgs>({
      query: ({ query }) => ({
        url: "marketplace/individual-bookmarks",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(BookmarkedIndividualListItemSchema.BookmarkedIndividualListItemJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (
        response: BookmarkedIndividualListItemJSON[],
        meta,
      ): Paginated<BookmarkedIndividualListItemJSON> => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
      providesTags: getIndividualBookmarkListTags,
    }),
    getIndividualBookmarksByIndividualIds: build.query<ByIdsResultJSON<BookmarkedIndividualJSON>, { individualIds: Guid[] }>({
      query: ({ individualIds }) => ({
        url: "marketplace/individual-bookmarks/by-individual-ids",
        method: "POST",
        body: { individualIds: individualIds.map((individualId) => individualId.value) },
      }),
      rawResponseSchema: byIdsResultJSONZ(BookmarkedIndividualSchema.BookmarkedIndividualJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { individualIds }) => getIndividualBookmarksByIndividualIdsTags(individualIds),
    }),
    getTeamBookmarks: build.query<Paginated<BookmarkedTeamListItemJSON>, TeamBookmarkListArgs>({
      query: ({ query }) => ({
        url: "marketplace/team-bookmarks",
        params: query.searchParams,
      }),
      rawResponseSchema: z.array(BookmarkedTeamListItemSchema.BookmarkedTeamListItemJSONZ),
      onSchemaFailure: logSchemaFailure,
      transformResponse: (
        response: BookmarkedTeamListItemJSON[],
        meta,
      ): Paginated<BookmarkedTeamListItemJSON> => ({
        data: response,
        headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
      }),
      providesTags: getTeamBookmarkListTags,
    }),
    getTeamBookmarksByTeamIds: build.query<ByIdsResultJSON<BookmarkedTeamJSON>, { teamIds: Guid[] }>({
      query: ({ teamIds }) => ({
        url: "marketplace/team-bookmarks/by-team-ids",
        method: "POST",
        body: { teamIds: teamIds.map((teamId) => teamId.value) },
      }),
      rawResponseSchema: byIdsResultJSONZ(BookmarkedTeamSchema.BookmarkedTeamJSONZ),
      onSchemaFailure: logSchemaFailure,
      providesTags: (_result, _error, { teamIds }) => getTeamBookmarksByTeamIdsTags(teamIds),
    }),
    // #endregion

    // #region Mutations
    createCompanyBookmark: build.mutation<BookmarkedCompanyJSON, { companyId: string }>({
      query: ({ companyId }) => ({
        url: `marketplace/company-bookmark/${companyId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedCompanySchema.BookmarkedCompanyJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    /** `companyId` names the company, so that the bookmarks slice can mark it as not bookmarked. */
    deleteCompanyBookmark: build.mutation<void, { bookmarkId: string; companyId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/company-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    createIndividualBookmark: build.mutation<BookmarkedIndividualJSON, { individualId: string }>({
      query: ({ individualId }) => ({
        url: `marketplace/individual-bookmark/${individualId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedIndividualSchema.BookmarkedIndividualJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    /** `individualId` names the person, so that the bookmarks slice can mark them as not bookmarked. */
    deleteIndividualBookmark: build.mutation<void, { bookmarkId: string; individualId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/individual-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    createTeamBookmark: build.mutation<BookmarkedTeamJSON, { teamId: string }>({
      query: ({ teamId }) => ({
        url: `marketplace/team-bookmark/${teamId}`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: BookmarkedTeamSchema.BookmarkedTeamJSONZ,
      onSchemaFailure: logSchemaFailure,
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    /** `teamId` names the team, so that the bookmarks slice can mark it as not bookmarked. */
    deleteTeamBookmark: build.mutation<void, { bookmarkId: string; teamId: string }>({
      query: ({ bookmarkId }) => ({
        url: `marketplace/team-bookmark/${bookmarkId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "Bookmark" as const, id: "LIST" }],
    }),
    // #endregion
  }),
});

export const {
  useGetCompanyBookmarksQuery,
  useGetCompanyBookmarksByCompanyIdsQuery,
  useGetIndividualBookmarksQuery,
  useGetIndividualBookmarksByIndividualIdsQuery,
  useGetTeamBookmarksQuery,
  useGetTeamBookmarksByTeamIdsQuery,
  useCreateCompanyBookmarkMutation,
  useDeleteCompanyBookmarkMutation,
  useCreateIndividualBookmarkMutation,
  useDeleteIndividualBookmarkMutation,
  useCreateTeamBookmarkMutation,
  useDeleteTeamBookmarkMutation,
} = bookmarksApi;
